/**
 * Header notification bell.
 *
 * Sits in the header's icon row (see `Layout.tsx`), next to the
 * theme controls — not on the homepage. Three layouts were tried on the homepage
 * first (a shared list, separate cards, a single rotating line) and all of them
 * fought for attention against the hero. Moving it into the nav, as a badge-count
 * icon that opens a dropdown on demand, keeps announcements available everywhere
 * without ever occupying space unless someone asks for it.
 *
 * Click-away / Escape / route-change handling mirrors `MobileMenu.tsx`.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { getActiveNotifications } from '@/lib/notifications'
import { NOTIFICATION_VISUALS } from './notificationVisuals'

/** However many notifications exist, the dropdown only ever lists the newest 5. */
const MAX_LISTED = 5

export const NotificationBell = () => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()

  const active = getActiveNotifications()
  const listed = active.slice(0, MAX_LISTED)

  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    const onClickAway = (event: MouseEvent) => {
      const target = event.target as Node
      if (!panelRef.current?.contains(target) && !buttonRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClickAway)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClickAway)
    }
  }, [open])

  // Nothing to announce: no icon, no badge, no dead chrome in the header.
  if (active.length === 0) return null

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className={`indicator btn btn-ghost btn-sm btn-square ${open ? 'text-primary' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="notification-bell-menu"
        aria-label={`Announcements (${active.length} active)`}
        title="Announcements"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell size={18} aria-hidden="true" />
        <span className="badge indicator-item badge-primary badge-xs px-1">{active.length}</span>
      </button>

      {/*
        Fixed + viewport-anchored below `lg`, not `absolute right-0` — this
        button sits well left of the header's true right edge (the "Build
        resume" CTA and the mobile hamburger are still further right), so
        anchoring the panel's right edge to *this button* instead of the
        viewport put most of a 320px panel off-screen to the left on a phone.
        `left-4 right-4` (viewport margins) fixes that regardless of where the
        trigger sits. `lg:` matches the header's own breakpoint for when the
        icon cluster stops being the only thing in the row.
      */}
      {open && (
        <div
          ref={panelRef}
          id="notification-bell-menu"
          role="menu"
          aria-label="Announcements"
          className="anim-pop fixed left-4 right-4 top-(--app-header-h) z-50 mt-2 overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-2xl lg:absolute lg:left-auto lg:right-0 lg:top-full lg:w-80"
        >
          <p className="border-b border-base-300 px-3 py-2 text-xs font-semibold opacity-60">Announcements</p>

          <ul className="max-h-80 divide-y divide-base-300 overflow-y-auto">
            {listed.map((n) => {
              const { icon: Icon, className } = NOTIFICATION_VISUALS[n.type]
              return (
                <li key={n.id} role="none">
                  <div role="menuitem" className="px-3 py-2.5 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Icon
                        size={14}
                        className={`shrink-0 ${className} ${n.priority === 'high' ? 'anim-attention' : ''}`}
                        aria-hidden="true"
                      />
                      <span className="font-semibold">{n.title}</span>
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs opacity-70">{n.message}</p>
                    {n.link && (
                      <Link to={n.link} className="link link-primary mt-1 inline-block text-xs font-medium">
                        Read more →
                      </Link>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>

          <Link
            to="/announcements"
            role="menuitem"
            className="block border-t border-base-300 px-3 py-2 text-center text-sm font-medium link link-hover"
          >
            View all →
          </Link>
        </div>
      )}
    </div>
  )
}
